<template>
  <div class="plano-contas-page">
    <!-- Header -->
    <div class="page-header">
      <div class="page-header-row">
        <div>
          <h1 class="page-title">{{ t('Plano de Contas') }}</h1>
          <p class="page-subtitle">
            {{ t('Gerencie as categorias de despesas e seus valores previstos.') }}
            <span class="page-subtitle-hint">
              {{ t('O plano ativo será usado como base de comparação nos fechamentos mensais.') }}
            </span>
          </p>
        </div>
        <div class="page-header-actions">
          <Button
            :label="t('Novo')"
            icon="pi pi-plus"
            severity="primary"
            :disabled="isLoading"
            data-testid="plano-contas-create-button"
            data-tutorial="btn-create-plano-contas"
            @click="openCreateModal"
          />
        </div>
      </div>
    </div>

    <!-- Table -->
    <box type="primary">
      <PlanoContasTable
        ref="planoContasTableRef"
        :planos="planos"
        :loading="isLoading"
        :refreshButton="{ show: true }"
        data-tutorial="plano-contas-table"
        @clone="onCloneClick"
        @toggle-status="onToggleStatus"
        @edit-click="onEditClick"
        @refresh="loadData"
      />

      <!-- Empty state -->
      <div v-if="!isLoading && planos.length === 0" class="empty-state">
        <i class="pi pi-folder-open" style="font-size: 3rem; color: #d1d5db;"></i>
        <p class="empty-state-text">{{ t('Nenhum plano de contas encontrado para as franquias selecionadas') }}</p>
      </div>
    </box>

    <!-- Note: ConfirmDialog is provided globally in App.vue - do not add duplicate here -->
    <!-- Note: PlanoContasModal is registered in ModalManager.vue, opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePlanoContas } from '../../composables/usePlanoContas';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';

type PlanoId = number | string;

interface Plano {
  id: PlanoId;
  franquia_id?: PlanoId;
  status?: string;
  is_active?: boolean;
  nome?: string;
  [key: string]: unknown;
}

interface ToggleStatusPayload {
  data: Plano;
  newValue: boolean;
}

interface PlanoContasTableInstance {
  loadData: () => void;
}

const { t } = useI18n();
const { confirmAction } = useConfirmAction();
const {
  isLoading,
  fetchPlanos,
  togglePlanStatus,
} = usePlanoContas();

const planos = ref<Plano[]>([]);
const planoContasTableRef = ref<PlanoContasTableInstance | null>(null);

const loadData = async (): Promise<void> => {
  log('PlanoContasPage', 'loadData', 'Begin load');
  try {
    eventBus.emit('loading.show', t('Carregando planos de contas...'));
    const data = (await fetchPlanos()) as Plano[];
    planos.value = data.map(p => ({
      ...p,
      is_active: p.status === 'active'
    }));
    log('PlanoContasPage', 'loadData', 'Loaded plans', data.length);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    log('PlanoContasPage', 'loadData', 'Error', message);
    eventBus.emit('globalMessage.error', t('Erro ao carregar planos de contas: ') + message);
  } finally {
    eventBus.emit('loading.hide');
  }
};

const performToggleStatus = async (data: Plano, newValue: boolean): Promise<void> => {
  try {
    eventBus.emit('loading.show', t('Atualizando status...'));
    await togglePlanStatus(Number(data.id), newValue ? '1' : '0');
    eventBus.emit('globalMessage.show', {
      type: 'success',
      title: t('Sucesso'),
      message: newValue ? t('Plano ativado com sucesso') : t('Plano desativado com sucesso')
    });
    await loadData();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    log('PlanoContasPage', 'performToggleStatus', 'Error', err);
    eventBus.emit('globalMessage.error', t('Erro ao atualizar status: ') + message);
    await loadData();
  } finally {
    eventBus.emit('loading.hide');
  }
};

const onToggleStatus = async ({ data, newValue }: ToggleStatusPayload): Promise<void> => {
  log('PlanoContasPage', 'onToggleStatus', 'Toggle requested', { data, newValue });

  if (newValue === true) {
    confirmAction({
      message: t('Ao ativar este plano, o plano ativo atual desta franquia será desativado. Deseja continuar?'),
      header: t('Confirmar ativação'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('Sim, ativar'),
      rejectLabel: t('Cancelar'),
      onAccept: async () => {
        await performToggleStatus(data, newValue);
      },
      onReject: async () => {
        planos.value = [];
        await nextTick();
        await loadData();
      }
    });
  } else {
    await performToggleStatus(data, newValue);
  }
};

const onCloneClick = (plano: Plano): void => {
  log('PlanoContasPage', 'onCloneClick', 'Cloning plan', plano);
  eventBus.emit('modal.plano-contas.open', { mode: 'clone', plan: plano });
};

const openCreateModal = (): void => {
  log('PlanoContasPage', 'openCreateModal', 'Opening create modal');
  eventBus.emit('modal.plano-contas.open', { mode: 'create' });
};

const onEditClick = (planoId: PlanoId): void => {
  log('PlanoContasPage', 'onEditClick', 'Viewing plan', planoId);
  const plan = planos.value.find(p => p.id === planoId);
  if (!plan) {
    eventBus.emit('globalMessage.error', t('Plano não encontrado'));
    return;
  }
  eventBus.emit('modal.plano-contas.open', { mode: 'view', plan });
};

const handleFilterUpdated = (): void => {
  log('PlanoContasPage', 'event:filter.updated', 'Received');
  loadData();
};

const handlePlanSaved = (): void => {
  log('PlanoContasPage', 'event:planocontas.cloned', 'Received');
  loadData();
};

onMounted(async () => {
  log('PlanoContasPage', 'mounted', 'Component mounted, loading initial data');

  await loadData();

  eventBus.on('filter.updated', handleFilterUpdated);
  eventBus.on('planocontas.cloned', handlePlanSaved);
});

onBeforeUnmount(() => {
  log('PlanoContasPage', 'beforeUnmount', 'Cleaning up listeners');
  eventBus.off('filter.updated', handleFilterUpdated);
  eventBus.off('planocontas.cloned', handlePlanSaved);
});
</script>

<style scoped>
/* ========================================
   PAGE LAYOUT
   ======================================== */
.plano-contas-page {
  padding: 1rem;
}

.page-header {
  margin-bottom: 20px;
}

.page-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-header-actions {
  display: flex;
  gap: 10px;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.page-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
}

.page-subtitle-hint {
  display: block;
  margin-top: 4px;
  font-size: 0.8rem;
  color: #9ca3af;
}

/* ========================================
   EMPTY / LOADING STATES
   ======================================== */
.empty-state {
  text-align: center;
  padding: 40px 0;
}

.empty-state-text {
  margin-top: 12px;
  color: #6b7280;
}

/* ========================================
   RESPONSIVE
   ======================================== */
@media (max-width: 768px) {
  .page-header-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}
</style>
